import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Landing page after Stripe checkout. Polls the payment status; on success it refreshes the auth state
// (so the newly-granted 15-day pass is picked up) and returns to the scanner, where the report unblurs.
export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const started = useRef(false);
  const [message, setMessage] = useState("Confirming your payment…");

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Cancelled checkout (or no session): let the user know and return to their blurred report.
    if (!sessionId) {
      if (location.pathname.includes("cancel")) {
        toast.message("Checkout cancelled — your report is still waiting.");
      }
      navigate("/", { replace: true });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") {
          await checkAuth();
          toast.success("Payment confirmed — your 15-day pass is active!");
          navigate("/", { replace: true });
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          toast.error("Payment was not completed.");
          navigate("/", { replace: true });
          return;
        }
      } catch (e) {
        // transient — keep polling
        console.warn("Payment status poll failed; retrying.", e);
      }
      if (attempts >= 15) {
        setMessage("Still processing — you can return and refresh shortly.");
        toast.message("Payment is still processing. Please check back in a moment.");
        setTimeout(() => navigate("/", { replace: true }), 2500);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, navigate, checkAuth, location.pathname]);

  return (
    <div data-testid="payment-return" className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
          <ShieldCheck className="h-7 w-7 text-primary" />
        </div>
        <div className="flex items-center gap-2 font-heading text-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm tracking-wide">{message}</span>
        </div>
      </div>
    </div>
  );
}
